import { useForm } from 'react-hook-form';
import moment from 'moment';
import { formatMultiline, formatOffer } from '@@viewHelpers/format.jsx';
import { applySpec, compose, prop, propOr, when } from 'ramda';
import { dateTsToDate } from '@@viewHelpers/date';
import { isNotNilOrEmpty } from '@@serverHelpers/index.mjs';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import useSaveSimulationEffectDateMutation from './useSaveSimulationEffectDateMutation.js';
import getExtraEffectDateDescription from './extra/getExtraEffectDateDescription.js';
import { useEffect, useState } from 'react';
import setExtraEffectDateMin from './extra/setExtraEffectDateMin.js';
import useGetSimulationEffectDateQuery from './useGetSimulationEffectDateQuery.js';

const useSimulationEffectDateForm = ({ getSimulationEffectDate, getMiddleText }) => {
  const { processHandlers, context } = useProcess();
  const [effectDateDescription, setEffectDateDescription] = useState('');

  const { onFormSubmit, loading: submitLoading } = useSaveSimulationEffectDateMutation();
  const { refetch } = useGetSimulationEffectDateQuery();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      effectDate: formatOffer(getSimulationEffectDate).effectDate,
      contractOnGoing: propOr(false, 'contractOnGoing', getSimulationEffectDate)
    }
  });

  const {
    watch,
    formState: { isValid }
  } = form;

  const effectDate = watch('effectDate');

  const { defaultMinEffectDate, maxEffectDate, offerCode, displayEffectDateError } = applySpec({
    defaultMinEffectDate: compose(when(isNotNilOrEmpty, dateTsToDate), prop('minEffectDate')),
    maxEffectDate: compose(when(isNotNilOrEmpty, dateTsToDate), prop('maxEffectDate')),
    displayEffectDateError: prop('displayEffectDateError'),
    offerCode: prop('offerCode')
  })(getSimulationEffectDate);

  const [minEffectDate, setMinEffectDate] = useState(defaultMinEffectDate);

  const dateIsCorrect = (date) => date && moment(date).isAfter(minEffectDate);

  const contractOnGoing = form.watch('contractOnGoing');

  useEffect(() => {
    setEffectDateDescription(
      formatMultiline(
        getExtraEffectDateDescription({
          description: getMiddleText('PROCESSDEVENTE_EFFECT_DATE_DESCRIPTION'),
          contractOnGoing
        })
      )
    );

    setExtraEffectDateMin(contractOnGoing, defaultMinEffectDate, setMinEffectDate);
  }, [contractOnGoing]);

  const onSubmit = (values) => {
    if (submitLoading) {
      return;
    }
    onFormSubmit(values, (ok, error) => {
      if (ok && !error) {
        refetch();
        processHandlers.onNext({
          context: {
            projectId: context?.projectId
          }
        });
      }
    });
  };

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  return {
    form,
    dateIsCorrect,
    effectDate,
    effectDateDescription,
    minEffectDate,
    maxEffectDate,
    isValid,
    onSubmit,
    offerCode,
    $onPrevClick,
    displayEffectDateError,
    submitLoading
  };
};

export default useSimulationEffectDateForm;
